import uuid from "uuid";

import {
  AuthorizationContextInput,
  CreateItemInput,
  NoExtraProperties,
  Resource,
  ResourceVisibilityType,
} from "@examspark/types";

import { GraphQLAPI } from "../client";
import { item } from "../queries";
import { typedGql, typedGqlMutation } from "../utils";

export const getItemForClone = async (
  itemId: string,
  authorizationContext: AuthorizationContextInput,
) => {
  const result = await typedGql(item.getItemForClone)({
    input: { id: itemId, sk: "item#v0" },
    authorizationContext,
  });

  return result.data?.getItem;
};

export const getItemForExam = async (
  itemId: string,
  authorizationContext: AuthorizationContextInput,
) => {
  const result = await GraphQLAPI.client.query<{
    getItem: Resource | null | undefined;
  }>({
    query: item.getItemForExam,
    variables: {
      input: { id: itemId, sk: "item#v0" },
      authorizationContext,
    },
  });

  return result.data?.getItem;
};

export const createItem = async <T extends CreateItemInput>(
  input: NoExtraProperties<CreateItemInput, T>,
  authorizationContext: AuthorizationContextInput,
) => {
  const result = await typedGqlMutation(item.createItem)({
    input,
    authorizationContext,
  });

  return result.data?.createItem;
};

/**
 * This particular mode of duplicating an item is for a teacher wanting to edit
 * a copy of their own item based on an existing item that is owned by another
 * entity.
 */
export const duplicateItemForCampfireUser = async (
  itemId: string,
  targetBankId: string,
  authorizationContext: AuthorizationContextInput,
) => {
  if (!itemId) return;
  const itemData = await getItemForClone(itemId, authorizationContext);
  if (!itemData) throw new Error("Item not found");

  const newItemData = JSON.parse(JSON.stringify(itemData));

  // Pass it its oldest possible ancestor? PS: Is this how it should work?
  if (itemData.originResourceId) {
    newItemData.originIdInc = itemData.originIdInc;
    newItemData.originResourceId = itemData.originResourceId;
    newItemData.originWorkspaceId = itemData.workspaceId;
    newItemData.originBankId = itemData.bankId;
  } else {
    newItemData.originIdInc = itemData.idInc;
    newItemData.originResourceId = [itemData.id, itemData.sk].join("/");
    newItemData.originWorkspaceId = itemData.workspaceId;
    newItemData.originBankId = itemData.bankId;
  }

  newItemData.id = uuid();
  newItemData.alignmentIds = itemData.alignmentIds ?? [];
  newItemData.creatorId = authorizationContext.userId;
  newItemData.contextIds = itemData.contextIds ?? [];
  newItemData.maturity = "OPERATIONAL";
  newItemData.visibility = ResourceVisibilityType.Private;
  newItemData.bankId = targetBankId;
  newItemData.workspaceId = authorizationContext.workspaceId;
  newItemData.name = (itemData.name ?? "") + " (Copy)";

  const newItemCreateResponse = await createItem(
    newItemData,
    authorizationContext,
  );

  if (!newItemCreateResponse) throw new Error("Failed to create item");
  return newItemCreateResponse;
};
